import { useI18n } from "vue-i18n";
import { BigNumber, ethers } from "ethers";
import usePOLPrice from "~/stores/POLprice";
import useUsdToEuroRate from "~/stores/storeUsdEuroRate";
import { Auction, Currency, MarketType, Scarcity } from "fungi-types";

const useConvert = () => {
  const getMaticPriceInUsdWei = () => ethers.utils.parseUnits(`${usePOLPrice().rates.usd}`, 18);

  function formatUsd(value: number, symbol = true): string {
    const props = symbol
      ? {
          style: "currency",
          currency: "USD",
          currencyDisplay: "narrowSymbol",
        }
      : {};
    return new Intl.NumberFormat(useI18n().locale.value, {
      maximumFractionDigits: 2,
      notation: "compact",
      ...props,
    }).format(value);
  }

  type convertType<T extends boolean> = T extends true ? string : number;

  function mtcToUsd<T extends boolean = true>(
    value: number,
    format: T = true as T
  ): convertType<T> {
    const result = value * usePOLPrice().rates.usd;
    if (format) {
      return new Intl.NumberFormat(useI18n().locale.value, {
        maximumFractionDigits: 2,
        notation: "compact",
        style: "currency",
        currency: "USD",
        currencyDisplay: "narrowSymbol",
      }).format(result) as convertType<T>;
    }
    return Number(result.toFixed(2)) as convertType<T>;
  }

  function usdToMtc<T extends boolean = true>(
    value: number,
    format: T = true as T,
    maximumFractionDigits = 2
  ): convertType<T> {
    const result = value / usePOLPrice().rates.usd;

    if (format) {
      return new Intl.NumberFormat(useI18n().locale.value, {
        maximumFractionDigits: maximumFractionDigits,
        notation: "compact",
      }).format(result) as convertType<T>;
    }
    return Number(result.toFixed(4)) as convertType<T>;
  }

  function weiToMatic(value: number | string | BigNumber) {
    return +ethers.utils.formatUnits(value, 18);
  }

  function weiToUsd(value: BigNumber) {
    const maticPriceInUsdWei = getMaticPriceInUsdWei();
    const usdWei = value.mul(maticPriceInUsdWei);
    return +ethers.utils.formatUnits(usdWei, 36);
  }

  function weiToUsdc(value: BigNumber | string) {
    const usdAmount = weiToUsd(BigNumber.from(value));
    return ethers.utils.parseUnits(usdAmount.toFixed(6), 6);
  }

  function usdToWei(value: number | string) {
    const maticPriceInUsdWei = getMaticPriceInUsdWei();
    const usdWei = ethers.utils.parseUnits(value.toString(), 36);
    return usdWei.div(maticPriceInUsdWei);
  }

  function maticToWei(value: number | BigNumber | string) {
    return ethers.utils.parseUnits(value.toString(), "18");
  }

  function cryptoToAllFiats({
    amount,
    fromCurrency,
  }: {
    amount: BigNumber | string;
    fromCurrency: Currency.USDC | Currency.WPOL | "wei";
  }): { eur: number; usd: number } {
    console.log("Converting", amount, fromCurrency);
    return {
      eur: cryptoToFiat(amount, fromCurrency, Currency.EUR),
      usd: cryptoToFiat(amount, fromCurrency, Currency.USD),
    };
  }

  function cryptoToFiat(
    amount: BigNumber | string,
    fromCurrency: Currency.USDC | Currency.WPOL | "wei",
    toCurrency: Currency.EUR | Currency.USD
  ): number {
    // Convert FROM USDC amount
    if (fromCurrency === Currency.USDC) {
      const usdAmount = +ethers.utils.formatUnits(amount, 6);
      if (toCurrency === Currency.USD) {
        return usdAmount;
      } else if (toCurrency === Currency.EUR) {
        const eurRate = useUsdToEuroRate().rates.usdToEur;
        return usdAmount * eurRate;
      }
    }

    // Convert From WPOL Amount
    else if (fromCurrency === Currency.WPOL) {
      const polAmount = +amount;
      if (toCurrency === Currency.USD) {
        return usePOLPrice().polToCurrency(polAmount, Currency.USD);
      } else if (toCurrency === Currency.EUR) {
        return usePOLPrice().polToCurrency(polAmount, Currency.EUR);
      }
    }
    // Convert FROM WEI Amount
    else if (fromCurrency === "wei") {
      const polAmount = +ethers.utils.formatUnits(amount, 18);
      if (toCurrency === Currency.USD) {
        return usePOLPrice().polToCurrency(polAmount, Currency.USD);
      } else if (toCurrency === Currency.EUR) {
        return usePOLPrice().polToCurrency(polAmount, Currency.EUR);
      }
    }

    throw new Error("Unsupported currency conversion");
  }

  type AuctionPrice = {
    fiat: {
      eur: number;
      usd: number;
    };
    crypto: {
      usdc: BigNumber;
      wpol: number;
      wei: BigNumber;
    };
  };

  function usdToUsdc(value: number): BigNumber {
    return ethers.utils.parseUnits(value.toFixed(6), 6);
  }

  function usdcToUsd(value: BigNumber | string): number {
    return +ethers.utils.formatUnits(value, 6);
  }

  function increaseMinBidPrice({
    prices,
    percentIncrease = 10,
  }: {
    prices: AuctionPrice;
    percentIncrease?: number;
  }): AuctionPrice {
    const multiplier = 1 + percentIncrease / 100;
    const bignumberMultiplier = Math.floor(multiplier * 100); // Pour garder 2 décimales de précision

    const resp = {
      fiat: {
        eur: prices.fiat.eur * multiplier,
        usd: prices.fiat.usd * multiplier,
      },
      crypto: {
        usdc: prices.crypto.usdc.mul(bignumberMultiplier).div(100),
        wpol: prices.crypto.wpol * multiplier,
        wei: prices.crypto.wei.mul(bignumberMultiplier).div(100),
      },
    };

    return formatDecimals(resp);
  }

  function getAuctionFloorPrice({ scarcity }: { scarcity: Scarcity }): AuctionPrice {
    const polPriceStore = usePOLPrice();
    const usdToEuroRate = useUsdToEuroRate().rates.usdToEur;
    let minUsdAmount = {
      [Scarcity.SILVER]: 2,
      [Scarcity.GOLD]: 16,
      [Scarcity.PLATINUM]: 75,
    };
    let minEuroAmount = {
      [Scarcity.SILVER]: minUsdAmount[Scarcity.SILVER] * usdToEuroRate,
      [Scarcity.GOLD]: minUsdAmount[Scarcity.GOLD] * usdToEuroRate,
      [Scarcity.PLATINUM]: minUsdAmount[Scarcity.PLATINUM] * usdToEuroRate,
    };
    let minWpolAmount = {
      [Scarcity.SILVER]: polPriceStore.currencyToPol(minEuroAmount[Scarcity.SILVER], Currency.EUR),
      [Scarcity.GOLD]: polPriceStore.currencyToPol(minEuroAmount[Scarcity.GOLD], Currency.EUR),
      [Scarcity.PLATINUM]: polPriceStore.currencyToPol(
        minEuroAmount[Scarcity.PLATINUM],
        Currency.EUR
      ),
    };
    let minUsdcAmount = {
      [Scarcity.SILVER]: ethers.utils.parseUnits(minUsdAmount[Scarcity.SILVER].toFixed(6), 6),
      [Scarcity.GOLD]: ethers.utils.parseUnits(minUsdAmount[Scarcity.GOLD].toFixed(6), 6),
      [Scarcity.PLATINUM]: ethers.utils.parseUnits(minUsdAmount[Scarcity.PLATINUM].toFixed(6), 6),
    };
    let minWeiAmount = {
      [Scarcity.SILVER]: polPriceStore.maticToWei(minWpolAmount[Scarcity.SILVER]),
      [Scarcity.GOLD]: polPriceStore.maticToWei(minWpolAmount[Scarcity.GOLD]),
      [Scarcity.PLATINUM]: polPriceStore.maticToWei(minWpolAmount[Scarcity.PLATINUM]),
    };
    let resp: AuctionPrice = {
      fiat: {
        eur: minEuroAmount[scarcity],
        usd: minUsdAmount[scarcity],
      },
      crypto: {
        usdc: minUsdcAmount[scarcity],
        wpol: minWpolAmount[scarcity],
        wei: minWeiAmount[scarcity],
      },
    };

    return formatDecimals(resp);
  }

  function getAuctionAmount(input: {
    marketType: MarketType.PRIMARY | MarketType.SECONDARY;
    scarcity: Scarcity;
    usdcAmount: BigNumber | string;
    weiAmount: BigNumber | string;
  }): AuctionPrice {
    const floorPrice = getAuctionFloorPrice({ scarcity: input.scarcity });

    const resp: AuctionPrice = {
      fiat: {
        eur: 0,
        usd: 0,
      },
      crypto: {
        usdc: BigNumber.from(0),
        wpol: 0,
        wei: BigNumber.from(0),
      },
    };

    const sourceUsdcAmount = BigNumber.from(input.usdcAmount);
    const sourceUsdcToWei = usdToWei(+ethers.utils.formatUnits(sourceUsdcAmount, 6));
    const sourceWeiAmount = BigNumber.from(input.weiAmount);
    const sourceWeiToUsdc = weiToUsdc(sourceWeiAmount);

    const weiAmount = sourceWeiAmount.gt(sourceUsdcToWei) ? sourceWeiAmount : sourceUsdcToWei;
    const usdcAmount = sourceUsdcAmount.gt(sourceWeiToUsdc) ? sourceUsdcAmount : sourceWeiToUsdc;

    resp.crypto.usdc = usdcAmount;
    resp.crypto.wei = weiAmount;
    resp.crypto.wpol = weiToMatic(weiAmount);

    const usdAmount = +ethers.utils.formatUnits(usdcAmount, 6);
    resp.fiat.usd = usdAmount;
    resp.fiat.eur = usdAmount * useUsdToEuroRate().rates.usdToEur;

    const maxBigNumber = (a: BigNumber, b: BigNumber) => (a.lt(b) ? b : a);
    const maxNumber = Math.max;

    if (input.marketType === MarketType.PRIMARY) {
      resp.crypto.usdc = maxBigNumber(resp.crypto.usdc, floorPrice.crypto.usdc);
      resp.crypto.wpol = maxNumber(resp.crypto.wpol, floorPrice.crypto.wpol);
      resp.crypto.wei = maxBigNumber(resp.crypto.wei, floorPrice.crypto.wei);
      resp.fiat.usd = maxNumber(resp.fiat.usd, floorPrice.fiat.usd);
      resp.fiat.eur = maxNumber(resp.fiat.eur, floorPrice.fiat.eur);
    }

    return formatDecimals(resp);
  }

  function formatAuctionPrice(
    value: number,
    currency: Currency.WPOL | Currency.EUR | Currency.USD
  ) {
    let resp: AuctionPrice = {
      fiat: {
        eur: 0,
        usd: 0,
      },
      crypto: {
        usdc: BigNumber.from(0),
        wpol: 0,
        wei: BigNumber.from(0),
      },
    };

    let usdcAmount = BigNumber.from(0);
    if (currency === Currency.EUR) {
      usdcAmount = usdToUsdc(value / useUsdToEuroRate().rates.usdToEur);
    } else if (currency === Currency.USD) {
      usdcAmount = usdToUsdc(value);
    } else if (currency === Currency.WPOL) {
      const usdValue = usePOLPrice().polToCurrency(value, Currency.USD);
      usdcAmount = usdToUsdc(usdValue);
    }

    resp.crypto.usdc = usdcAmount;
    resp.crypto.wei = usdToWei(value);
    resp.crypto.wpol = weiToMatic(resp.crypto.wei);

    const usdAmount = usdcToUsd(usdcAmount);
    resp.fiat.usd = usdAmount;
    resp.fiat.eur = usdAmount * useUsdToEuroRate().rates.usdToEur;

    return formatDecimals(resp);
  }

  function formatDecimals(prices: AuctionPrice) {
    prices.fiat.eur = parseFloat(prices.fiat.eur.toFixed(2));
    prices.fiat.usd = parseFloat(prices.fiat.usd.toFixed(2));
    prices.crypto.wpol = parseFloat(prices.crypto.wpol.toFixed(2));
    return prices;
  }

  return {
    formatUsd,
    mtcToUsd,
    usdToMtc,
    weiToMatic,
    cryptoToFiat,
    cryptoToAllFiats,
    maticToWei,
    weiToUsd,
    usdToWei,
    usdcToUsd,
    usdToUsdc,

    // auction
    getAuctionFloorPrice,
    getAuctionAmount,
    increaseMinBidPrice,
    formatAuctionPrice,
  };
};

export default useConvert();
